import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  BoxProps,
  Collapse,
  LinearProgress,
  LinearProgressProps,
  Toolbar,
} from '@mui/material';

import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../constants';
import { getThemeMode } from '../../../store/ConfigSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { getIsLoading } from '../../../store/LoaderSlice';
import {
  getUsageByUserId,
  saveUserDetails,
} from '../../../store/AccessLimitSlice/AccessLimitApi';
import { createPremiumSession } from '../../../store/StripeSlice';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../store/AccessLimitSlice';
import { useAppSelector } from '../../../hooks';
import { checkUserPermission, SafeAreaGaps } from '../../../helper';
import NavigationBarHeader from './components/NavigationBarHeader';
import AboutQuiz from '../AboutQuiz';
import LeadGenModal, { ILeadGenState } from './modals/LeadGenModal';
import PremiumModal from './modals/PremiumModal';
import InvalidRoute from '../../Validate/InvalidRoute';
import SubmissionQuestionsManagement from '../SubmissionQuestionsManagement';
import AboutQuizHistoryPage from '../QuizHistory/AboutQuizHistoryPage';
import Diagnostic from '../Diagnostic';
import SubjectPage from '../Subject';
import MyQuizHistory from '../QuizHistory/MyQuizHistory';
import TopicPage from '../Topic';
import Resources from '../Resources';
import Students from '../../AdminPages/Students';
import StudentSubmissions from '../../AdminPages/Students/StudentSubmissions';
import ViewSubmissionQuestions from '../../AdminPages/Students/ViewSubmissionQuestions';
import PlanPage from '../PlanPage';
import ManageOldRoutes from '../../Validate/ManageOldRoutes';
import LoginAlertModal from './modals/LoginAlertModal';
import LeadGenModalForResources, {
  IResourceLeadGenState,
} from './modals/LeadGenModalForResources';
import { createAnalytics } from '../../../store/AnalyticsSlice';
import ViewQuizSubmission from '../ViewQuizSubmission';
import { getTenantsState } from '../../../store/V2/TenantsSlice';

export const isVideoContainer = (): boolean => {
  const element = document.getElementById('video-lib-container');
  if (!element) return false;

  const { bottom } = element.getBoundingClientRect();
  return bottom <= window.innerHeight;
};

const UserDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Store
  const themeMode = useAppSelector(getThemeMode);
  const authState = useAppSelector(getAuthState);
  const isLoading = useAppSelector(getIsLoading);
  const accessLimitState = useAppSelector(getAccessLimitState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  // State
  const [isLoginAlertModalOpen, setLoginAlertModalOpen] = useState(false);

  const queryStringParams = new URLSearchParams(location.search);
  const isGuestParam = Boolean(queryStringParams.get('isGuest'));
  const isQuizModal = window.location.href.includes('quiz');

  // Permissions
  const viewQuizPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      quizHistory: ['view'],
    },
  });

  const viewSubmissionPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      submission: ['view'],
    },
  });

  const adminStudentPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminStudent: ['view'],
    },
  });

  const mainStyles: BoxProps['sx'] = {
    flexGrow: 1,
    width: { sm: `100%` },
    color: 'text.primary',
    minHeight: { xs: `calc(100vh - 54px)`, sm: `calc(100vh - 64px)` },
    backgroundColor:
      themeMode === 'light' ? TP_COLORS.paperGray.light : 'background.paper',
  };

  useEffect(() => {
    if (
      authUser &&
      (authUser.role.name === 'Student' || authUser.role.name === 'Guest')
    ) {
      dispatch(getUsageByUserId(authUser._id));
    }

    if (isGuestParam) {
      setLoginAlertModalOpen(true);
    }
  }, []);

  const handleLeadGenSubmit = (values: ILeadGenState) => {
    if (values && authUser) {
      dispatch(
        saveUserDetails({
          ...values,
          userId: authUser._id,
        })
      );
    }
  };
  const handResourceleLeadGenSubmit = (values: IResourceLeadGenState) => {
    if (values && authUser) {
      dispatch(
        saveUserDetails({
          ...values,
          userId: authUser._id,
        })
      );
    }
  };

  const getClickedSource = () => {
    if (isQuizModal) return 'Quiz';
    if (isVideoContainer()) return 'Videos';
    return 'Content';
  };

  const handlePremiumModalSubmit = (priceId: string) => {
    if (authUser) {
      dispatch(
        createAnalytics({
          type: 'LP - Premium Popup',
          data: {
            selectedRole: authUser?.role.name,
            fullName: `${authUser.firstname} ${authUser.lastname || ''}`,
            phoneNumber: authUser.phoneNumber,
            country: authUser.country,
            source: getClickedSource(),
            url: location.pathname.replace('/learning-portal/dashboard/', ''),
          },
          userId: authUser?._id,
        })
      );
      dispatch(
        createPremiumSession({
          userId: authUser._id,
          priceId,
          returnUrl: location.pathname.replace('learning-portal/', ''),
        })
      );
    }
    dispatch(togglePremiumModal());
  };

  const RENDER_ROUTES = () => {
    return (
      <Routes>
        <Route path="/upgrade" element={<PlanPage />} />
        {viewSubmissionPermission ? (
          <>
            <Route
              path="/:curriculum/:subject/:topic/:subTopic/:subtopicId/view-quiz/:submissionId"
              element={<ViewQuizSubmission />}
            />
            <Route
              path="/:curriculum/:subject/:topic/:subTopic/:subtopicId/quiz-history/:quizId"
              element={<AboutQuizHistoryPage />}
            />
          </>
        ) : null}
        {viewSubmissionPermission ? (
          <>
            <Route
              path="/:curriculum/:subject/:subjectLevel/:subjectCode"
              element={<SubjectPage />}
            />
            <Route
              path="/:curriculum/:subject/:subjectLevel/:subjectCode/:topic/:topicId"
              element={<TopicPage />}
            />
            <Route
              path="/:curriculum/:subject/:topic/:subTopic/:subtopicId"
              element={<Resources />}
            />
            <Route
              path="/:curriculum/:subject/:topic/:subTopic/:subtopicId/quiz"
              element={<AboutQuiz />}
            />
            <Route
              path="/:curriculum/:subject/:topic/:subTopic/:subtopicId/quiz/:submissionId"
              element={<SubmissionQuestionsManagement />}
            />
            <Route
              path="/:curriculum/:subject/:topic/:subTopic/:subtopicId/diagnostic-quiz/:diagnosticId"
              element={<Diagnostic />}
            />
          </>
        ) : null}
        {/* Quiz History */}
        {viewQuizPermission && (
          <Route path="quiz-history" element={<MyQuizHistory />} />
        )}
        {/* Students */}
        {adminStudentPermission && (
          <>
            <Route path="students" element={<Students />} />
            <Route
              path="students/submissions/:userId"
              element={<StudentSubmissions />}
            />
            <Route
              path="students/viewQuestions/:userId/:submissionId"
              element={<ViewSubmissionQuestions />}
            />
          </>
        )}
        {/* Navigate to New Routes */}
        <Route path="/my-quizzes/resources/*" element={<ManageOldRoutes />} />
        <Route path="/my-quizzes/subtopic/*" element={<ManageOldRoutes />} />
        {/* Validate */}
        <Route path="*" element={<InvalidRoute />} />
      </Routes>
    );
  };

  const RENDER_MODALS = () => {
    return (
      <>
        {accessLimitState.leadGenModalOpen &&
          (isQuizModal ? (
            <LeadGenModal
              open={accessLimitState.leadGenModalOpen}
              onClose={() => dispatch(toggleLeadGenModal())}
              onConfirm={handleLeadGenSubmit}
            />
          ) : (
            <LeadGenModalForResources
              open={accessLimitState.leadGenModalOpen}
              onClose={() => dispatch(toggleLeadGenModal())}
              onConfirm={handResourceleLeadGenSubmit}
            />
          ))}
        {accessLimitState.premiumModalOpen && (
          <PremiumModal
            open={accessLimitState.premiumModalOpen}
            onClose={() => dispatch(togglePremiumModal())}
            onConfirm={handlePremiumModalSubmit}
          />
        )}
        {isLoginAlertModalOpen && (
          <LoginAlertModal
            open={isLoginAlertModalOpen}
            onClose={() => setLoginAlertModalOpen(false)}
          />
        )}
      </>
    );
  };

  // Styles
  const loaderStyles: LinearProgressProps['sx'] = {
    position: 'fixed',
    zIndex: 1,
    top: {
      xs: `calc(${SafeAreaGaps.top} + 56px)`,
      sm: `calc(${SafeAreaGaps.top} + 64px)`,
    },
    width: '100%',
  };

  // RENDER
  return (
    <div>
      <Box>
        <CssBaseline />

        {/* Header */}
        <NavigationBarHeader />
        <Toolbar />

        {/* Body */}
        <Box component="main" sx={mainStyles}>
          <Collapse in={isLoading} sx={loaderStyles}>
            <LinearProgress color="info" />
          </Collapse>
          {RENDER_ROUTES()}
          {RENDER_MODALS()}
        </Box>
      </Box>
    </div>
  );
};

export default UserDashboard;
